import { useGoBack } from 'hooks/useGoBack';
import React, { useCallback, useContext } from 'react';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { AppContext, IAppContext } from 'appState/appContext';
import { convertUndefinedFields } from 'utils/helpers';
import { EditTeaserContext, IEditTeaserContext } from '../../editTeaserState/editTeaserContext';
import { editTeaser } from 'backend/teasers';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { refetchData, changeTypeOfModalWindow, dataForSubmit, typeOfModalWindow } =
		useContext<IEditTeaserContext>(EditTeaserContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow });
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/teasers');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await editTeaser(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				changeTypeOfModalWindow('error');
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'imageFileSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '1MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'videoFileSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '50MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'fileUploadError'}
				description="fileUploadError"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'invalidFileFormat'}
				description="invalidFileFormatPhotoVideo"
				translationOptions={{ photoFormats: '.jpeg, .jpg, .png', videoFormats: '.mp4' }}
			/>
		</div>
	);
};

export default ModalWindows;
